import type { FeatureRegistry } from './feature-registry.js';
import { foundationSystems } from './systems.js';

/** A navigation node. Leaves carry a route; branches may also be navigable. */
export interface NavNode {
  readonly label: string;
  readonly route?: string;
  readonly children?: readonly NavNode[];
}

/** A collapsible sidebar section (a domain). */
export interface NavSection {
  readonly id: string;
  readonly label: string;
  /** Whether the section starts expanded. Daily stays open; domains start calm/closed. */
  readonly defaultOpen: boolean;
  readonly items: readonly NavNode[];
}

/**
 * The domain-organized sidebar (v1 depth, v2 calm). "Daily" is pinned open;
 * domains start collapsed so the surface stays calm but everything is one
 * click + a disclosure away — and also reachable via Search and the palette.
 */
export const navigationTree: readonly NavSection[] = [
  {
    id: 'daily',
    label: 'Daily',
    defaultOpen: true,
    items: [
      { label: 'Today', route: '/today' },
      { label: 'Capture', route: '/capture' },
      { label: 'Tasks', route: '/tasks' },
      { label: 'Calendar & Schedule', route: '/calendar' },
      { label: 'Routines & Habits', route: '/routines' },
      { label: 'Targets & Streaks', route: '/targets' },
      { label: 'Reminders', route: '/reminders' },
      { label: 'Reviews', route: '/reviews' },
      { label: 'Focus', route: '/focus' },
    ],
  },
  {
    id: 'discover',
    label: 'Discover',
    defaultOpen: true,
    items: [
      { label: 'Search', route: '/search' },
      { label: 'All Systems', route: '/all-systems' },
    ],
  },
  {
    id: 'direction',
    label: 'Direction',
    defaultOpen: false,
    items: [
      {
        label: 'Direction',
        route: '/direction',
        children: [
          { label: 'Roles & Relationships', route: '/direction/roles' },
          { label: 'Values & Mindsets', route: '/direction/values' },
          { label: 'Challenges & Opportunities', route: '/direction/challenges' },
        ],
      },
      { label: 'Problems & Repair', route: '/problems' },
    ],
  },
  {
    id: 'build',
    label: 'Build & Learn',
    defaultOpen: false,
    items: [
      { label: 'Projects', route: '/projects' },
      {
        label: 'Knowledge',
        route: '/knowledge',
        children: [
          { label: 'Notes', route: '/knowledge/notes' },
          { label: 'Knowledge Graph', route: '/knowledge/graph' },
          { label: 'Source Review', route: '/source-review' },
        ],
      },
      { label: 'Study & Learning', route: '/study' },
      { label: 'Academics & Funding', route: '/academics' },
      { label: 'Career & Identity', route: '/career' },
      { label: 'Work & Content', route: '/work' },
    ],
  },
  {
    id: 'life',
    label: 'Life',
    defaultOpen: false,
    items: [
      { label: 'Health & Body', route: '/health' },
      { label: 'Finance Vault', route: '/finance' },
      { label: 'Business & Ventures', route: '/business' },
      { label: 'Mental', route: '/mental' },
      { label: 'Spiritual', route: '/spiritual' },
      { label: 'Environment & Devices', route: '/environment' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    defaultOpen: false,
    items: [
      { label: 'Assistant', route: '/assistant' },
      { label: 'Runtime Status', route: '/runtime' },
      { label: 'Security & Lock', route: '/security' },
    ],
  },
];

/** Every route referenced anywhere in the nav tree (flattened). */
export function navigationRoutes(tree: readonly NavSection[] = navigationTree): string[] {
  const out: string[] = [];
  const walk = (nodes: readonly NavNode[]) => {
    for (const n of nodes) {
      if (n.route) out.push(n.route);
      if (n.children) walk(n.children);
    }
  };
  for (const s of tree) walk(s.items);
  return out;
}

/** Alias used by tooling/doctors: all routes reachable from the nav tree. */
export function navRoutes(): string[] {
  return navigationRoutes();
}

/** Validate that every nav route maps to a registered system (no dead links). */
export function validateNavigation(registry: FeatureRegistry): string[] {
  const registered = new Set(
    registry
      .all()
      .filter((s) => s.surface.kind === 'route')
      .map((s) => (s.surface as { path: string }).path),
  );
  return navigationRoutes().filter((r) => !registered.has(r));
}

/** Convenience for tooling that does not want to build a registry. */
export function navigationRoutesNotInSystems(): string[] {
  const registered = new Set(
    foundationSystems
      .filter((s) => s.surface.kind === 'route')
      .map((s) => (s.surface as { path: string }).path),
  );
  return navigationRoutes().filter((r) => !registered.has(r));
}
